import { Dictionary } from '@feedyou/schema'
import { ISSMLCheckTagError } from 'ssml-check'

type AlwaysConstraint = { type: 'always' }
type EnumConstraint = { type: 'values'; enum: Set<string> }
type RangeConstraint = { type: 'range'; from: number; to: number; unit: string }
type ValueConstraint = RangeConstraint | EnumConstraint | AlwaysConstraint

type Attribute = string

// We need to define a large number of fine-grained exceptions to the ssml-check validation rules.
// This schema lets us define constraints that specific `tags -> attributes -> values` should adhere to.
//
// This way, we can define different schemas that describe for example:
// - all ssml tag errors that we want to ignore and treat as valid ssml
// - all ssml tag errors that we want to treat as warnings only
//
// See the usage of this type to get a better idea :)
export type TagIssueConstraintSchema = Array<{
  tag: string
  constraints: ValueConstraint[] | Dictionary<Attribute, ValueConstraint[]>
}>

export const doesIssueSatisfySchema = (
  { tag, attribute, value }: ISSMLCheckTagError,
  schemaForTag: TagIssueConstraintSchema[number] | undefined
) => {
  const constraintsForTag = schemaForTag?.constraints

  if (!constraintsForTag) {
    return false
  }

  const activeConstraints = Array.isArray(constraintsForTag)
    ? constraintsForTag
    : constraintsForTag[attribute]!

  return !!activeConstraints?.some(constraint => doesValueSatisfyConstraint(value, constraint))
}

function doesValueSatisfyConstraint(value: string, constraint: ValueConstraint): boolean {
  switch (constraint.type) {
    case 'always':
      return true
    case 'values':
      return doesValueSatisfyExactConstraint(value, constraint)
    case 'range':
      return doesValueSatisfyRangeConstraint(value, constraint)
    default:
      throw new Error('Invalid value constraint type')
  }
}

function doesValueSatisfyExactConstraint(value: string, constraint: EnumConstraint): boolean {
  return constraint.enum.has(value?.toLowerCase())
}

function doesValueSatisfyRangeConstraint(value: string, constraint: RangeConstraint): boolean {
  const { to, from, unit } = constraint

  if (!value.endsWith(unit)) {
    return false
  }

  const valueWithoutUnit = value.replace(new RegExp(`${unit}$`), '')
  const isValueFloat = new RegExp('^[+-]?\\d+(\\.\\d+)?$').test(valueWithoutUnit)
  if (!isValueFloat) {
    return false
  }

  const valueAsFloat = parseFloat(valueWithoutUnit)
  return from <= valueAsFloat && to >= valueAsFloat
}
